import React, { useState } from 'react';
import Link from 'components/link';
import { trackEvent } from 'utils/google-tag-manager';

import {
  useFeaturesContext,
  useChat,
  useModalContext,
  useThemeContext,
  useUser,
  useUserContext,
} from 'contexts';
import styles from './style.module.scss';
import { testIds } from 'constants/test-constants';
import { translate } from 'utils/translations';
import {
  MENU_LINK_LABEL_MY_SEARCHES,
  MENU_LINK_LABEL_SIGN_IN,
  ThemeNames,
} from '@tenants/types';
import { useRouter } from 'next/router';
import { buildClassName } from 'utils/build-class-name';
import { ReadonlyDeep } from 'type-fest';
import NestedDropdown from './nested-dropdown';
import { openEqualWebMenu } from 'utils/accessibility';
import { ACCESSIBILITY_PATH } from 'constants/urls';

export type DropdownProps = ReadonlyDeep<{
  className?: string;
  items: {
    id: string;
    label: string;
    content?: React.ReactNode;
    link?: string;
    isLink?: boolean;
    isEmber?: boolean;
    newTab?: boolean;
    mobileOnly?: boolean;
    signOut?: boolean;
    onClick?: () => void;
    nested?: {
      id: string;
      label: string;
      link?: string;
      newTab?: boolean;
    }[];
  }[];
}>;

export default function Dropdown({ items, className }: DropdownProps) {
  const { features } = useFeaturesContext();
  const { openChat } = useChat();
  const { openModal } = useModalContext();
  const { siteLocation, signOut } = useUser();
  const { themeName } = useThemeContext();
  const { isAuthenticated } = useUserContext();
  const router = useRouter();
  const isZoocasaTenant = themeName === ThemeNames.ZOOCASA;
  const [expandedItems, setExpandedItems] = useState<{[key: string]: boolean }>({});

  const itemsWithDefaults = items.map(item => ({
    ...item,
    isLink: item.isLink ?? true,
  }));

  const handleAccessibilityClick = () => {
    openEqualWebMenu();
  };

  const toggleExpand = (id: string) => {
    setExpandedItems(prev => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  return (
    <div className={buildClassName(styles.component, !isZoocasaTenant && styles.exp, className)}>
      <ul className={styles['menu-items']}>
        {itemsWithDefaults.map((item, index) => {
          if (item.nested && Array.isArray(item.nested) && item.nested.length > 0) {
            return <NestedDropdown key={index} item={item} index={index} expandedItems={expandedItems} toggleExpand={toggleExpand} siteLocation={siteLocation}/>;
          } else if (item.signOut || item.label === 'Sign Out') {
            return (
              <li key={index}>
                <a onClick={signOut} data-testid={item.id + testIds.menuDropdownOption}>{item.label}</a>
              </li>
            );
          } else if (item.isEmber) {
            if (item.link === 'https://www.zoocasa.com/blog/') {
              if (isZoocasaTenant) {
                return (
                  <li key={index}>
                    <a href={item.link} data-testid={item.id + testIds.menuDropdownOption}>{item.label}</a>
                  </li>
                );
              }
              return null;
            }
            return (
              <li key={index}>
                <a onClick={item.label == 'Edit Profile' ? () => trackEvent('UiProfilepView') : () => {}} href={item.link} data-testid={item.id + testIds.menuDropdownOption}>{item.label}</a>
              </li>
            );
          } else if (item.link === ACCESSIBILITY_PATH) {
            return (
              <li key={index} tabIndex={0}>
                <a onClick={handleAccessibilityClick} data-testid={item.id + testIds.menuDropdownOption}>{item.label}</a>
              </li>
            );
          } else if (item.label === MENU_LINK_LABEL_SIGN_IN) {
            return (
              <li key={index}>
                {isAuthenticated ?
                  <a onClick={signOut}>Sign Out</a>
                  :
                  <a onClick={() => openModal('login-registration')}>Sign In</a>
                }
              </li>
            );
          } else if (item.label === MENU_LINK_LABEL_MY_SEARCHES) {
            return (
              !isAuthenticated && !isZoocasaTenant ?
                <></>
                :
                <li key={index}>
                  {!isAuthenticated ?
                    <a onClick={() => openModal('login-registration', {
                      callback: () => router.push(item.link as string),
                    })}>
                      My Searches
                    </a>
                    :
                    <Link href={item.link as string}>
                      My Searches
                    </Link>
                  }
                </li>
            );
          } else if (item.link === '/newhomes') {
            if (features.usePreCon) {
              return (
                <li key={index}>
                  <Link href={item.link || '/'} onClick={() => trackEvent('precon-nav-click')} testId={item.id + testIds.menuDropdownOption}>New Construction</Link>
                </li>
              );
            } else {
              return null;
            }
          } else if (item.label === 'Chat') {
            return (
              <li key={index}>
                <a onClick={() => openChat({ source: 'Menu' })} data-testid={item.id + testIds.menuDropdownOption}>{item.label}</a>
              </li>
            );
          } else {
            const itemContent = item.content ? (
              <span className={styles['menu-item-with-content']}>
                {translate(item.label, siteLocation)}
                {item.content}
              </span>
            ) : (
              translate(item.label, siteLocation)
            );

            return (
              <li key={index} className={item.mobileOnly && styles.mobile}>
                {item.isLink ?
                  <Link href={item.link || '/'} target={item.newTab && '_blank'} onClick={item.onClick} testId={item.id + testIds.menuDropdownOption}>
                    {itemContent}
                  </Link>
                  :
                  <a onClick={item.onClick}>
                    {itemContent}
                  </a>}
              </li>
            );
          }
        })}
      </ul>
    </div>
  );
}
